/**
 * The mutable refs the chat flows share (send, tool calls, document analysis,
 * voice transcription and loadMessages all read and write them). They live in
 * one stable bundle so every extracted hook sees the same boxes; splitting
 * them per hook would let the flows disagree about the session on screen or
 * the operation in flight (fe_chat_library.md "Extraction risk").
 */
import { useMemo, useRef } from 'react';

import type { ChatMessage, ToolCall, ToolResult } from '../../types';

export function useChatOperationRefs() {
  // Contextual tips system
  const messageCountRef = useRef(0);
  const sessionHasAnalysisRef = useRef(false);

  // AI tool calls waiting for the user (upload / voice) and their results so far.
  const pendingToolCallsRef = useRef<ToolCall[]>([]);
  const pendingToolIntentRef = useRef<string | null>(null);
  const pendingUploadToolRef = useRef<ToolCall | null>(null);
  const pendingVoiceToolRef = useRef<ToolCall | null>(null);
  const pendingToolResultsRef = useRef<ToolResult[]>([]);
  const pendingVoiceToolParamsRef = useRef<Record<string, any> | null>(null);
  const handleToolCallsRef = useRef<(toolCalls: ToolCall[], originIntent?: string) => void>(() => {});
  const pickAudioForTranscriptionRef = useRef<() => void>(() => {});
  const isToolSubmitInProgressRef = useRef(false);

  // Task ids the user cancelled (their late results are ignored).
  const cancelledTaskIdsRef = useRef<Set<string>>(new Set());

  const optimisticMessagesRef = useRef<Map<number, ChatMessage[]>>(new Map());
  const taskWaiterRef = useRef<Set<string>>(new Set());
  const shouldAutoScrollRef = useRef(true);
  const pendingScrollToBottomRef = useRef(false);

  // Track which session has an active operation (to show thinking bubble only for that session)
  const activeOperationSessionRef = useRef<number | null>(null);

  // Synchronous guards to prevent double-execution of async operations
  // React state updates are async, so we need refs for immediate blocking
  const isAnalyzingInProgressRef = useRef(false);
  const isSendingInProgressRef = useRef(false);

  // Bug fix: synchronous flag to suppress thinking bubble re-insertion
  // when loadMessages is called right after an operation completes.
  // React state (analyzingFile/sendingMessage) hasn't re-rendered yet,
  // so loadMessages still sees stale true values via its closure.
  const suppressBubbleRef = useRef(false);

  // Refs mirroring state for use inside loadMessages (prevents dep array churn)
  const sendingMessageRef = useRef(false);
  const analyzingFileRef = useRef(false);

  // Every member is a ref, so the bundle never changes identity.
  return useMemo(() => ({
    messageCountRef,
    sessionHasAnalysisRef,
    pendingToolCallsRef,
    pendingToolIntentRef,
    pendingUploadToolRef,
    pendingVoiceToolRef,
    pendingToolResultsRef,
    pendingVoiceToolParamsRef,
    handleToolCallsRef,
    pickAudioForTranscriptionRef,
    isToolSubmitInProgressRef,
    cancelledTaskIdsRef,
    optimisticMessagesRef,
    taskWaiterRef,
    shouldAutoScrollRef,
    pendingScrollToBottomRef,
    activeOperationSessionRef,
    isAnalyzingInProgressRef,
    isSendingInProgressRef,
    suppressBubbleRef,
    sendingMessageRef,
    analyzingFileRef,
  }), []);
}

export type ChatOperationRefs = ReturnType<typeof useChatOperationRefs>;
